import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { H1 } from '../../components/Typography';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Calendar, X, Grid, List, ChevronRight, Archive, Trash2 } from 'react-feather';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { collection, query, orderBy, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { auth } from '../../config/firebase';
import { bulkArchiveOrders, bulkDeleteOrders } from '../../api/orders';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { sl } from 'date-fns/locale'; // Slovenian locale
import 'react-big-calendar/lib/css/react-big-calendar.css';

const OrdersContainer = styled.div`
  width: 100%;
  padding: 0 2rem;
  max-width: none !important;
  margin: 0 !important;

  @media (min-width: 1200px) {
    max-width: var(--admin-max-width) !important;
    margin: 0 auto !important;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--color-text);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const FilterButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--color-text);
  cursor: pointer;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  background: transparent;
  border: none;
  color: ${props => props.$active ? 'var(--color-primary)' : 'var(--color-text)'};
  border-bottom: 2px solid ${props => props.$active ? 'var(--color-primary)' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: var(--color-primary);
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 2rem;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
`;

const OrderCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const OrderName = styled.h3`
  font-size: var(--font-size-lg);
  margin: 0;
`;

const OrderDate = styled.span`
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.6);
`;

const OrderDetails = styled.div`
  font-size: var(--font-size-sm);
  
  p {
    margin: 0.5rem 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: var(--font-size-xs);
  background: ${props => {
    switch (props.$status) {
      case 'new': return 'rgba(0, 255, 0, 0.2)';
      case 'contacted': return 'rgba(255, 165, 0, 0.2)';
      case 'scheduled': return 'rgba(0, 191, 255, 0.2)';
      case 'completed': return 'rgba(128, 128, 128, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'new': return 'rgba(0, 255, 0, 0.3)';
      case 'contacted': return 'rgba(255, 165, 0, 0.3)';
      case 'scheduled': return 'rgba(0, 191, 255, 0.3)';
      case 'completed': return 'rgba(128, 128, 128, 0.3)';
      default: return 'rgba(255, 255, 255, 0.2)';
    }
  }};
`;

const formatPhoneNumber = (phone) => {
  return phone ? phone.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3') : 'N/A';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'new': return { bg: 'rgba(0, 255, 0, 0.2)', border: 'rgba(0, 255, 0, 0.3)' };
    case 'contacted': return { bg: 'rgba(255, 165, 0, 0.2)', border: 'rgba(255, 165, 0, 0.3)' };
    case 'scheduled': return { bg: 'rgba(0, 191, 255, 0.2)', border: 'rgba(0, 191, 255, 0.3)' };
    case 'completed': return { bg: 'rgba(128, 128, 128, 0.2)', border: 'rgba(128, 128, 128, 0.3)' };
    default: return { bg: 'rgba(255, 255, 255, 0.1)', border: 'rgba(255, 255, 255, 0.2)' };
  }
};

const FilterPopover = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  width: 300px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const FilterGroup = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: var(--font-size-sm);
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--color-text);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .react-datepicker-wrapper {
    width: auto;
  }

  .react-datepicker {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    font-family: inherit;
  }

  .react-datepicker__header {
    background: #242424;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__day {
    color: var(--color-text);
  }

  .react-datepicker__day:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .react-datepicker__day--selected {
    background: var(--color-primary);
    color: black;
  }
`;

const DateInput = styled.input`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  width: 120px;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ApplyButton = styled(motion.button)`
  width: 100%;
  padding: 0.75rem;
  background: var(--color-primary);
  border: none;
  border-radius: 6px;
  color: black;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const FilterTag = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: var(--font-size-xs);

  button {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-text);
    cursor: pointer;
    display: flex;
    align-items: center;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const ViewButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.$isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$isActive ? 'var(--color-primary)' : 'var(--color-text)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const TableView = styled.div`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 2rem;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 100px;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 100px;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const TableCheckbox = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
  margin: 0;
`;

const BulkActionsBar = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const BulkActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.$variant === 'delete' ? 'rgba(255, 59, 48, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$variant === 'delete' ? 'rgba(255, 59, 48, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$variant === 'delete' ? '#ff3b30' : 'var(--color-text)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--font-size-sm);
`;

// Add localization setup
const locales = {
  'sl': sl,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Add styled component for calendar
const CalendarWrapper = styled.div`
  margin-top: 2rem;
  
  .rbc-calendar {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: var(--radius-lg);
    padding: 1rem;
    min-height: 600px;
  }

  .rbc-header {
    color: var(--color-text);
    padding: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .rbc-event {
    background: var(--color-primary);
    border: none;
  }

  .rbc-today {
    background: rgba(255, 255, 255, 0.05);
  }

  .rbc-off-range-bg {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const validateSchedulingData = (order) => {
  if (!order.scheduling) {
    console.warn('No scheduling data:', order.id);
    return false;
  }
  
  if (!order.scheduling.date || !order.scheduling.time) {
    console.warn('Missing date or time:', order.id, order.scheduling);
    return false;
  }

  // Parse date safely
  const scheduledDate = new Date(order.scheduling.date);
  if (isNaN(scheduledDate.getTime())) {
    console.warn('Invalid date format:', order.id, order.scheduling.date);
    return false;
  }

  // Validate time format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(order.scheduling.time)) {
    console.warn('Invalid time format:', order.id, order.scheduling.time);
    return false;
  }

  return true;
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [filters, setFilters] = useState({
    style: '',
    isFirstTattoo: '',
    location: '',
    startDate: null,
    endDate: null
  });
  const [viewMode, setViewMode] = useState('card'); // 'card', 'table', or 'calendar'
  
  const filterRef = useRef(null);
  const dateRef = useRef(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  useEffect(() => {
    if (auth.currentUser) {
      fetchOrders();
    }
  }, [auth.currentUser]);

  useEffect(() => {
    filterOrders();
  }, [orders, activeTab, searchTerm, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOrders = async () => {
    try {
      console.log('Fetching orders...');
      if (!auth.currentUser) {
        console.log('Waiting for auth...');
        return;
      }

      console.log('User authenticated:', auth.currentUser.email);
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('created_at', 'desc'));
      
      console.log('Executing query...');
      const snapshot = await getDocs(q);
      
      console.log('Raw snapshot:', snapshot);
      const data = snapshot.docs.map(doc => {
        console.log('Document data:', doc.data());
        return {
          id: doc.id,
          ...doc.data()
        };
      });

      console.log('Processed orders:', data);
      setOrders(data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      console.error('Error details:', err.code, err.message);
      setError(err.message);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Filter by status
    if (activeTab !== 'all') {
      filtered = filtered.filter(order => order.status === activeTab);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order => {
        const searchString = `${order.personal_info?.fullName} ${order.personal_info?.email} ${order.tattoo_details?.style}`.toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      });
    }

    // Apply additional filters
    if (filters.style) {
      filtered = filtered.filter(order => 
        order.tattoo_details?.style?.toLowerCase() === filters.style.toLowerCase()
      );
    }

    if (filters.isFirstTattoo) {
      filtered = filtered.filter(order => 
        order.tattoo_details?.isFirstTattoo === filters.isFirstTattoo
      );
    }

    if (filters.location) {
      filtered = filtered.filter(order => 
        order.tattoo_details?.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= filters.startDate && orderDate <= filters.endDate;
      });
    }

    setFilteredOrders(filtered);
  };

  const handleApplyFilters = () => {
    filterOrders();
    setShowFilters(false);
    setShowDatePicker(false);
  };

  const clearFilter = (filterKey) => {
    setFilters(prev => ({ ...prev, [filterKey]: '' }));
  };

  const clearDateRange = () => {
    setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
  };

  const getStatusCount = (status) => {
    return orders.filter(order => order.status === status).length;
  };

  const handleOrderClick = (orderId) => {
    navigate(`/admin/orders/${orderId}`);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (e, orderId) => {
    e.stopPropagation(); // Prevent row click when checking checkbox
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (e.target.checked) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  };

  const handleBulkArchive = async () => {
    if (window.confirm(`Archive ${selectedOrders.size} orders?`)) {
      try {
        const result = await bulkArchiveOrders(Array.from(selectedOrders));
        if (result.success) {
          setSelectedOrders(new Set());
          fetchOrders(); // Refresh the list
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Error archiving orders:', error);
        // Could add toast notification here
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Delete ${selectedOrders.size} orders? This action cannot be undone.`)) {
      try {
        const result = await bulkDeleteOrders(Array.from(selectedOrders));
        if (result.success) {
          setSelectedOrders(new Set());
          fetchOrders(); // Refresh the list
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Error deleting orders:', error);
        // Could add toast notification here
      }
    }
  };

  // Update the calendar events transform to use scheduling data
  const getCalendarEvents = useMemo(() => {
    return filteredOrders
      .filter(order => {
        return order.status === 'scheduled' && validateSchedulingData(order);
      })
      .map(order => {
        const scheduledDate = new Date(order.scheduling.date);
        const [hours, minutes] = order.scheduling.time.split(':');
        
        const startTime = new Date(scheduledDate);
        startTime.setHours(parseInt(hours), parseInt(minutes));
        
        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + parseInt(order.scheduling.duration || 2));

        return {
          id: order.id,
          title: order.personal_info?.fullName || 'No Name',
          start: startTime,
          end: endTime,
          allDay: false,
          resource: {
            duration: order.scheduling.duration,
            notes: order.scheduling.notes,
            style: order.tattoo_details?.style,
            size: order.tattoo_details?.size,
            location: order.tattoo_details?.location,
            phone: order.personal_info?.phone
          }
        };
      });
  }, [filteredOrders]);

  return (
    <OrdersContainer>
      <Header>
        <H1>Orders</H1>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div ref={filterRef} style={{ position: 'relative' }}>
            <FilterButton 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filter
            </FilterButton>
            
            {showFilters && (
              <FilterPopover
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <FilterGroup>
                  <FilterLabel>Style</FilterLabel>
                  <FilterSelect
                    value={filters.style}
                    onChange={(e) => setFilters(prev => ({ ...prev, style: e.target.value }))}
                  >
                    <option value="">All Styles</option>
                    <option value="Traditional">Traditional</option>
                    <option value="Neo-traditional">Neo-traditional</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Minimalist">Minimalist</option>
                    <option value="Black and Grey">Black and Grey</option>
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>First Tattoo</FilterLabel>
                  <FilterSelect
                    value={filters.isFirstTattoo}
                    onChange={(e) => setFilters(prev => ({ ...prev, isFirstTattoo: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </FilterSelect>
                </FilterGroup>

                <ApplyButton
                  onClick={handleApplyFilters}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Apply Filters
                </ApplyButton>
              </FilterPopover>
            )}
          </div>

          <div ref={dateRef} style={{ position: 'relative' }}>
            <FilterButton 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Calendar size={16} />
              Date
            </FilterButton>

            {showDatePicker && (
              <FilterPopover
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <FilterGroup>
                  <FilterLabel>Date Range</FilterLabel>
                  <DateRangeContainer>
                    <DatePicker
                      selected={filters.startDate}
                      onChange={date => setFilters(prev => ({ ...prev, startDate: date }))}
                      selectsStart
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      placeholderText="Start Date"
                      customInput={<DateInput />}
                    />
                    <span>to</span>
                    <DatePicker
                      selected={filters.endDate}
                      onChange={date => setFilters(prev => ({ ...prev, endDate: date }))}
                      selectsEnd
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      minDate={filters.startDate}
                      placeholderText="End Date"
                      customInput={<DateInput />}
                    />
                  </DateRangeContainer>
                </FilterGroup>

                <ApplyButton
                  onClick={handleApplyFilters}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Apply Date Range
                </ApplyButton>
              </FilterPopover>
            )}
          </div>
          <ViewToggle>
            <ViewButton
              $isActive={viewMode === 'card'}
              onClick={() => setViewMode('card')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Card View"
            >
              <Grid size={18} />
            </ViewButton>
            <ViewButton
              $isActive={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Table View"
            >
              <List size={18} />
            </ViewButton>
            <ViewButton
              $isActive={viewMode === 'calendar'}
              onClick={() => setViewMode('calendar')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Calendar View"
            >
              <Calendar size={18} />
            </ViewButton>
          </ViewToggle>
        </SearchBar>
      </Header>

      {/* Show active filters */}
      {(filters.style || filters.isFirstTattoo || (filters.startDate && filters.endDate)) && (
        <ActiveFilters>
          {filters.style && (
            <FilterTag>
              Style: {filters.style}
              <button onClick={() => clearFilter('style')}><X size={12} /></button>
            </FilterTag>
          )}
          {filters.isFirstTattoo && (
            <FilterTag>
              First Tattoo: {filters.isFirstTattoo}
              <button onClick={() => clearFilter('isFirstTattoo')}><X size={12} /></button>
            </FilterTag>
          )}
          {filters.startDate && filters.endDate && (
            <FilterTag>
              Date: {filters.startDate.toLocaleDateString()} - {filters.endDate.toLocaleDateString()}
              <button onClick={clearDateRange}><X size={12} /></button>
            </FilterTag>
          )}
        </ActiveFilters>
      )}

      <TabsContainer>
        <Tab 
          $active={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All ({orders.length})
        </Tab>
        <Tab 
          $active={activeTab === 'new'} 
          onClick={() => setActiveTab('new')}
        >
          New ({getStatusCount('new')})
        </Tab>
        <Tab 
          $active={activeTab === 'contacted'} 
          onClick={() => setActiveTab('contacted')}
        >
          Contacted ({getStatusCount('contacted')})
        </Tab>
        <Tab 
          $active={activeTab === 'scheduled'} 
          onClick={() => setActiveTab('scheduled')}
        >
          Scheduled ({getStatusCount('scheduled')})
        </Tab>
        <Tab 
          $active={activeTab === 'completed'} 
          onClick={() => setActiveTab('completed')}
        >
          Completed ({getStatusCount('completed')})
        </Tab>
      </TabsContainer>

      {viewMode === 'card' ? (
        <OrdersGrid>
          {filteredOrders.map((order) => {
            const personalInfo = order.personal_info;
            const tattooDetails = order.tattoo_details;
            const statusColors = getStatusColor(order.status);

            return (
              <OrderCard
                key={order.id}
                onClick={() => handleOrderClick(order.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
              >
                <OrderHeader>
                  <OrderName>{personalInfo?.fullName || 'No Name'}</OrderName>
                  <StatusBadge 
                    $status={order.status} 
                    style={{
                      background: statusColors.bg,
                      borderColor: statusColors.border
                    }}
                  >
                    {order.status || 'New'}
                  </StatusBadge>
                </OrderHeader>

                <OrderDetails>
                  <p>Email: {personalInfo?.email || 'N/A'}</p>
                  <p>Phone: {formatPhoneNumber(personalInfo?.phone)}</p>
                  <p>Style: {tattooDetails?.style || 'N/A'}</p>
                  <p>Size: {tattooDetails?.size || 'N/A'}</p>
                  <OrderDate>
                    {new Date(order.created_at).toLocaleDateString('sl-SI', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </OrderDate>
                </OrderDetails>
              </OrderCard>
            );
          })}
        </OrdersGrid>
      ) : viewMode === 'table' ? (
        <>
          {selectedOrders.size > 0 && (
            <BulkActionsBar
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span>{selectedOrders.size} selected</span>
              <BulkActionButton
                onClick={handleBulkArchive}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Archive size={14} />
                Archive Selected
              </BulkActionButton>
              <BulkActionButton
                $variant="delete"
                onClick={handleBulkDelete}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trash2 size={14} />
                Delete Selected
              </BulkActionButton>
            </BulkActionsBar>
          )}
          <TableView>
            <TableHeader>
              <div>
                <TableCheckbox
                  type="checkbox"
                  checked={selectedOrders.size === filteredOrders.length}
                  onChange={handleSelectAll}
                />
              </div>
              <div>Customer</div>
              <div>Date</div>
              <div>Style</div>
              <div>Status</div>
              <div></div>
            </TableHeader>
            {filteredOrders.map((order) => (
              <TableRow
                key={order.id}
                onClick={() => handleOrderClick(order.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ x: 5 }}
              >
                <div onClick={e => e.stopPropagation()}>
                  <TableCheckbox
                    type="checkbox"
                    checked={selectedOrders.has(order.id)}
                    onChange={(e) => handleSelectOrder(e, order.id)}
                  />
                </div>
                <div>
                  <strong>{order.personal_info?.fullName || 'No Name'}</strong>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    {order.personal_info?.email}
                  </div>
                </div>
                <div>{new Date(order.created_at).toLocaleDateString('sl-SI')}</div>
                <div>{order.tattoo_details?.style || 'N/A'}</div>
                <div>
                  <StatusBadge $status={order.status}>
                    {order.status || 'New'}
                  </StatusBadge>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <ChevronRight size={16} style={{ opacity: 0.5 }} />
                </div>
              </TableRow>
            ))}
          </TableView>
        </>
      ) : viewMode === 'calendar' && (
        <CalendarWrapper>
          <BigCalendar
            localizer={localizer}
            events={getCalendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            onSelectEvent={(event) => handleOrderClick(event.id)}
            tooltipAccessor={(event) => `
              ${event.title}
              Lokacija: ${event.resource.location}
              Stil: ${event.resource.style}
              Velikost: ${event.resource.size}
              Trajanje: ${event.resource.duration}h
              Tel: ${event.resource.phone}
              ${event.resource.notes ? `Opombe: ${event.resource.notes}` : ''}
            `}
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: 'var(--color-primary)',
                border: 'none',
                borderRadius: '4px'
              }
            })}
            views={['month', 'week', 'day', 'agenda']}
            messages={{
              today: 'Danes',
              previous: 'Nazaj',
              next: 'Naprej',
              month: 'Mesec',
              week: 'Teden',
              day: 'Dan',
              agenda: 'Seznam',
              noEventsInRange: 'Ni terminov v tem obdobju'
            }}
          />
        </CalendarWrapper>
      )}
    </OrdersContainer>
  );
};

export default Orders;
